import React from 'react';
import NewSeasonModal from './NewSeasonModal';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

// Storybook metadata
export default {
  title: 'Components/Seasons/NewSeasonModal',
  component: NewSeasonModal,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      `\nThe \`NewSeasonModal\` component allows users to create new seasons by providing essential details like name, start date, and end date.
        \n\n###_**Key Features:**_
        \n- Validates required fields like name, start date, and end date.
        \n- Provides intuitive date pickers for selecting start and end dates.
        \n- Highlights validation errors to guide users during input.\n\n`,
      // When & how to use it?
      `\n\nUse the \`NewSeasonModal\` component when users need to add a new season.
        \n\n###_**Key Features:**_
        \n- **Dynamic Validation**:
        \n  - Highlights missing fields in red when the form is submitted without all required inputs.
        \n\n- **User-Friendly Input Fields**:
        \n  - Includes a text input for the season name and two date pickers for start and end dates.
        \n\n- **Submit Button**:
        \n  - Triggers the creation of a new season when all fields are valid.\n\n`,
      // Current Usage
      '\nThe `NewSeasonModal` component is used on the Seasons page to enable users to create new seasons. It ensures that all necessary fields are filled before submitting.\n\n',
      // Related Components
      `\n- **[Modal](http://localhost:6006/?path=/docs/components-seasons-modal--docs)**: Base modal component for rendering the modal overlay.
        \n- **[DateSelectorV2](http://localhost:6006/?path=/docs/components-inputs-dateselectorv2--docs)**: Provides date selection functionality for the start and end dates.
        \n- **[TextInput](http://localhost:6006/?path=/docs/components-inputs-textinput--docs)**: Provides an input field for entering the new season's name.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    isShowing: {
      control: { type: 'boolean' },
      description: 'Controls the visibility of the modal.',
      table: {
        type: { summary: 'boolean' },
        required: true,
      },
    },
    handleClose: {
      control: { type: 'function' },
      description: 'Callback function to close the modal.',
      table: {
        type: { summary: 'function' },
        required: false,
      },
    },
    onCreate: {
      control: { type: 'function' },
      description: 'Callback function triggered when creating a new season.',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
  },
};

// Template for the stories
const Template = (args) => (
  <div style={{
    height: '50vh', width: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}
  >
    <NewSeasonModal {...args} />
  </div>
);

// Default story for the modal
export const DefaultNewSeasonModal = Template.bind({});
DefaultNewSeasonModal.args = {
  isShowing: true,
  handleClose: () => {},
  onCreate: () => {},
};
DefaultNewSeasonModal.parameters = generateStoryDocs(
  // When is this variant used?
  '\nUse this variant to display the modal for creating a new season.\n\n',
  // Where is it currently used on the product?
  '\nThe `NewSeasonModal` component is used on the Seasons page to add new seasons.\n\n',
);
